import * as THREE from "three";

interface Particle {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
  vel: THREE.Vector3;
  shrink: boolean;
  grow: boolean;
  gravity: boolean;
  drag: number;
  // Extra opacity scaler on top of the normal life-based fade (default 1 —
  // only tread dust dials this down, so overlapping puffs don't compound
  // into a much denser fog than any single puff looks like on its own).
  opacityMult?: number;
}

interface Ring {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
  maxR: number;
}

interface Incoming {
  shell: THREE.Mesh;
  warn: THREE.Mesh;
  life: number;
  maxLife: number;
  sx: number;
  sz: number;
  tx: number;
  tz: number;
  peak: number;
}

interface Glow {
  light: THREE.PointLight;
  life: number;
  maxLife: number;
  maxIntensity: number;
}

// Shared unit geometry + materials so pooled spheres are interchangeable: a
// particle's size is controlled entirely by mesh.scale (reset on spawn), never
// by the geometry. Colors are set per-spawn on the (cloned-free) material.
const SPARK_GEO = new THREE.SphereGeometry(1, 8, 8);
const SMOKE_GEO = new THREE.SphereGeometry(1, 8, 8);
const RING_GEO = new THREE.RingGeometry(0.7, 1.0, 32);
const DECAL_GEO = new THREE.PlaneGeometry(0.8, 0.8);

/** Soot/scorch-mark texture (canvas-generated, irregular so it doesn't read
 * as a perfect circle) for persistent wall/obstacle impact decals. */
function makeScorchTexture(): THREE.CanvasTexture {
  const s = 64;
  const c = document.createElement("canvas");
  c.width = s;
  c.height = s;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  // Charred black core fading through a lighter ash-grey halo before going
  // fully transparent — the halo is what keeps the mark readable against both
  // bright (concrete/sandbag) AND dark/shadowed (dusk-lit wall) surfaces; a
  // plain dark blotch all but disappears against the game's dim dusk tones.
  g.addColorStop(0, "rgba(8,6,5,0.85)");
  g.addColorStop(0.45, "rgba(15,12,10,0.65)");
  g.addColorStop(0.72, "rgba(120,108,92,0.3)");
  g.addColorStop(1, "rgba(120,108,92,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(s / 2, s / 2, s / 2, 0, Math.PI * 2);
  ctx.fill();
  // A few darker irregular blotches so it doesn't read as a perfect disc.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.random();
    const r = s * 0.28;
    ctx.fillStyle = "rgba(10,8,6,0.35)";
    ctx.beginPath();
    ctx.arc(s / 2 + Math.cos(a) * r, s / 2 + Math.sin(a) * r, s * (0.12 + Math.random() * 0.1), 0, Math.PI * 2);
    ctx.fill();
  }
  return new THREE.CanvasTexture(c);
}

// Lazily built (not at module scope) — this file is unit-tested under a plain
// "node" vitest environment (no `document`), and impactDecal() is the only
// thing that needs canvas access, so we defer it to first real use instead of
// requiring a DOM at import time.
let scorchMat: THREE.MeshBasicMaterial | null = null;
function getScorchMat(): THREE.MeshBasicMaterial {
  if (!scorchMat) {
    scorchMat = new THREE.MeshBasicMaterial({
      map: makeScorchTexture(), transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2,
    });
  }
  return scorchMat;
}

/**
 * Transient-FX system: muzzle flashes, sparks, smoke, shockwave rings and the
 * lobbed mortar shell. Sparks/smoke are pooled unit spheres that drift, fade,
 * and (optionally) shrink/grow/fall. Rings are flat expanding toruses. The
 * incoming mortar is a parabolic shell + ground warning marker, animated from
 * spawn until its travel time elapses (the detonation boom is a separate call).
 * Impact decals (scorch marks) are separate: persistent for the match (not
 * pooled/expiring), capped so they can't grow unbounded over a long game.
 */
export class Effects {
  private parts: Particle[] = [];
  private rings: Ring[] = [];
  private incoming: Incoming[] = [];
  private glows: Glow[] = [];
  private decals: THREE.Mesh[] = [];
  private readonly MAX_DECALS = 60;
  private sparkPool: THREE.Mesh[] = [];
  private smokePool: THREE.Mesh[] = [];
  private ringPool: THREE.Mesh[] = [];
  private glowPool: THREE.PointLight[] = [];

  constructor(private scene: THREE.Scene) {}

  /**
   * A persistent scorch-mark decal at a wall/obstacle impact point, oriented
   * to face back along the incoming projectile's travel direction (an
   * approximation of the surface normal that needs no raycast against the
   * hit geometry — accurate for axis-aligned box obstacles/walls hit
   * head-on, close enough at a glance otherwise). Capped at MAX_DECALS,
   * oldest-first, so a long match can't accumulate unbounded geometry.
   */
  impactDecal(x: number, y: number, z: number, vx: number, vz: number): void {
    const mat = getScorchMat().clone();
    const m = new THREE.Mesh(DECAL_GEO, mat);
    const len = Math.hypot(vx, vz) || 1;
    const nx = -vx / len, nz = -vz / len;
    const normal = new THREE.Vector3(nx, 0, nz);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
    m.position.set(x + nx * 0.03, y, z + nz * 0.03);
    m.rotateZ(Math.random() * Math.PI * 2);
    this.scene.add(m);
    this.decals.push(m);
    if (this.decals.length > this.MAX_DECALS) {
      const old = this.decals.shift()!;
      this.scene.remove(old);
      (old.material as THREE.Material).dispose();
    }
  }


  /** Brief expanding flash at the muzzle, plus a real dynamic light so nearby
   * geometry (walls, other tanks) actually catches the flash. */
  muzzleFlash(x: number, y: number, z: number): void {
    const m = this.spark(0xffe066);
    m.position.set(x, y, z);
    m.scale.setScalar(0.35);
    this.parts.push({ mesh: m, life: 0.09, maxLife: 0.09, vel: new THREE.Vector3(), shrink: true, grow: false, gravity: false, drag: 1 });

    const maxIntensity = 6;
    let light = this.glowPool.pop();
    if (!light) light = new THREE.PointLight(0xffcc66, maxIntensity, 9, 2);
    light.position.set(x, y, z);
    light.intensity = maxIntensity;
    this.scene.add(light);
    this.glows.push({ light, life: 0.07, maxLife: 0.07, maxIntensity });
  }

  /** Outward spark burst on a bullet impact. `scale` sizes the burst. */
  impact(x: number, y: number, z: number, scale = 1, color = 0xffaa44): void {
    const n = Math.round(7 * scale);
    for (let i = 0; i < n; i++) {
      const m = this.spark(i % 3 === 0 ? 0xffd166 : color);
      m.position.set(x, y, z);
      m.scale.setScalar(0.22 * scale);
      const a = Math.random() * Math.PI * 2;
      const sp = (6 + Math.random() * 6) * scale;
      this.parts.push({
        mesh: m, life: 0.32, maxLife: 0.32, shrink: true, grow: false, gravity: true, drag: 0.92,
        vel: new THREE.Vector3(Math.cos(a) * sp, Math.random() * 5, Math.sin(a) * sp),
      });
    }
  }

  /** Medium boom: sparks + a bright core + a little smoke + a shock ring. */
  explosion(x: number, y: number, z: number, scale = 1): void {
    this.sparkBurst(x, y, z, Math.round(16 * scale), [0xffd166, 0xff7a1a], 8 + 11, 0.5);
    this.smokePuffs(x, y, z, Math.max(2, Math.round(3 * scale)), scale);
    this.core(x, y, z, 0.9 * scale, 0xfff2b0, 0.18);
    this.shockwave(x, z, 0xffd166, 5 * scale, 0.4);
  }

  /** Big boom (tank death / mortar detonation): fat sparks, heavy smoke, wide ring. */
  bigExplosion(x: number, y: number, z: number): void {
    this.sparkBurst(x, y, z, 30, [0xffe066, 0xff7a1a, 0xff3b3b], 22, 0.65);
    this.smokePuffs(x, y, z, 8, 2.4);
    this.core(x, y, z, 1.6, 0xfff2b0, 0.26);
    this.shockwave(x, z, 0xffb347, 9, 0.6);
    this.shockwave(x, z, 0xfff2b0, 5, 0.35);
  }

  /** Expanding ground shockwave ring at (x,z). */
  shockwave(x: number, z: number, color = 0xffd166, maxR = 6, dur = 0.45): void {
    const m = this.acquireRing(color);
    m.position.set(x, 0.05, z);
    m.scale.setScalar(0.4);
    this.rings.push({ mesh: m, life: dur, maxLife: dur, maxR });
  }

  /** Streak of sparks trailing a Scout dash. */
  dashTrail(x: number, z: number, rotY: number): void {
    const fx = Math.sin(rotY);
    const fz = Math.cos(rotY);
    for (let i = 0; i < 10; i++) {
      const back = i * 0.5;
      const m = this.spark(i % 2 ? 0x9ad8ff : 0xffffff);
      m.position.set(x - fx * back, 0.8 + Math.random() * 0.6, z - fz * back);
      m.scale.setScalar(0.2);
      const a = Math.random() * Math.PI * 2;
      this.parts.push({
        mesh: m, life: 0.3, maxLife: 0.3, shrink: true, grow: false, gravity: false, drag: 0.9,
        vel: new THREE.Vector3(Math.cos(a) * 2, Math.random() * 2, Math.sin(a) * 2),
      });
    }
  }

  /** Small tan dust puff kicked up behind a moving tank's tracks (throttled by
   * the caller). Deliberately subtle — smaller/shorter-lived and dimmer than a
   * generic smoke puff, since several of these overlap in quick succession
   * while a tank drives (see EntityManager's DUST_INTERVAL_MS), and that
   * compounding is what makes it read as an intense fog rather than a light
   * tread kick if each individual puff isn't kept small and faint. */
  dustPuff(x: number, y: number, z: number): void {
    const m = this.smoke(0xac9a72);
    m.position.set(x + (Math.random() - 0.5) * 0.6, y, z + (Math.random() - 0.5) * 0.6);
    m.scale.setScalar(0.16);
    const a = Math.random() * Math.PI * 2;
    this.parts.push({
      mesh: m, life: 0.35, maxLife: 0.35, shrink: false, grow: true, gravity: false, drag: 0.9, opacityMult: 0.45,
      vel: new THREE.Vector3(Math.cos(a) * 0.4, 0.4 + Math.random() * 0.25, Math.sin(a) * 0.4),
    });
  }

  /**
   * Lobbed mortar shell arcing from (sx,sz) to (tx,tz) over `travelMs`, plus a
   * pulsing ground warning ring at the target. The detonation FX are triggered
   * separately (bigExplosion) when the "mortar" message arrives.
   */
  mortarIncoming(sx: number, sz: number, tx: number, tz: number, travelMs: number): void {
    const shell = this.spark(0xffd166);
    shell.scale.setScalar(0.45);
    shell.position.set(sx, 1.5, sz);
    const warn = this.acquireRing(0xff3b3b);
    warn.position.set(tx, 0.06, tz);
    warn.scale.setScalar(1);
    const peak = 12 + Math.hypot(tx - sx, tz - sz) * 0.15;
    this.incoming.push({ shell, warn, life: travelMs / 1000, maxLife: travelMs / 1000, sx, sz, tx, tz, peak });
  }

  update(dt: number): void {
    // Sparks + smoke.
    for (let i = this.parts.length - 1; i >= 0; i--) {
      const p = this.parts[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.release(p.mesh);
        this.parts.splice(i, 1);
        continue;
      }
      if (p.gravity) p.vel.y -= 14 * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(p.drag);
      const t = p.life / p.maxLife;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = (mat.blending === THREE.AdditiveBlending ? t : t * 0.6) * (p.opacityMult ?? 1);
      if (p.shrink) p.mesh.scale.setScalar(Math.max(0.05, p.mesh.scale.x * 0.96));
      else if (p.grow) p.mesh.scale.multiplyScalar(1.04);
    }

    // Transient point lights (muzzle flash): quick linear decay then release.
    for (let i = this.glows.length - 1; i >= 0; i--) {
      const gl = this.glows[i];
      gl.life -= dt;
      if (gl.life <= 0) {
        this.scene.remove(gl.light);
        this.glowPool.push(gl.light);
        this.glows.splice(i, 1);
        continue;
      }
      gl.light.intensity = gl.maxIntensity * (gl.life / gl.maxLife);
    }

    // Shockwave rings: expand + fade.
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      r.life -= dt;
      if (r.life <= 0) {
        this.scene.remove(r.mesh);
        this.ringPool.push(r.mesh);
        this.rings.splice(i, 1);
        continue;
      }
      const t = 1 - r.life / r.maxLife;
      r.mesh.scale.setScalar(0.4 + t * r.maxR);
      (r.mesh.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.8;
    }

    // Lobbed mortar shells: parabolic arc + pulsing warning.
    for (let i = this.incoming.length - 1; i >= 0; i--) {
      const s = this.incoming[i];
      s.life -= dt;
      if (s.life <= 0) {
        this.scene.remove(s.shell);
        this.scene.remove(s.warn);
        this.release(s.shell);
        this.ringPool.push(s.warn);
        this.incoming.splice(i, 1);
        continue;
      }
      const f = 1 - s.life / s.maxLife; // 0 → 1 along the arc
      s.shell.position.set(
        s.sx + (s.tx - s.sx) * f,
        1.5 + 4 * s.peak * f * (1 - f), // parabola: 0 at ends, peak mid
        s.sz + (s.tz - s.sz) * f,
      );
      const pulse = 1 + Math.sin(f * Math.PI * 6) * 0.15;
      s.warn.scale.setScalar(pulse);
      (s.warn.material as THREE.MeshBasicMaterial).opacity = 0.5 + 0.4 * Math.sin(f * Math.PI * 6);
    }
  }

  // ── internal builders ──────────────────────────────────────────────

  private sparkBurst(
    x: number, y: number, z: number, n: number, colors: number[], speed: number, life: number,
  ): void {
    for (let i = 0; i < n; i++) {
      const m = this.spark(colors[i % colors.length]);
      m.position.set(x, y, z);
      m.scale.setScalar(0.3);
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.5 + Math.random() * 0.5);
      this.parts.push({
        mesh: m, life, maxLife: life, shrink: true, grow: false, gravity: true, drag: 0.93,
        vel: new THREE.Vector3(Math.cos(a) * sp, 3 + Math.random() * speed, Math.sin(a) * sp),
      });
    }
  }

  private smokePuffs(x: number, y: number, z: number, n: number, scale: number): void {
    for (let i = 0; i < n; i++) {
      const m = this.smoke();
      m.position.set(x + (Math.random() - 0.5) * scale, y + Math.random() * scale, z + (Math.random() - 0.5) * scale);
      m.scale.setScalar(0.6 * scale);
      const a = Math.random() * Math.PI * 2;
      this.parts.push({
        mesh: m, life: 0.9, maxLife: 0.9, shrink: false, grow: true, gravity: false, drag: 0.88,
        vel: new THREE.Vector3(Math.cos(a) * 2 * scale, 2 + Math.random() * 2, Math.sin(a) * 2 * scale),
      });
    }
  }

  private core(x: number, y: number, z: number, radius: number, color: number, life: number): void {
    const m = this.spark(color);
    m.position.set(x, y, z);
    m.scale.setScalar(radius);
    this.parts.push({ mesh: m, life, maxLife: life, vel: new THREE.Vector3(), shrink: false, grow: true, gravity: false, drag: 1 });
  }

  /** Additive spark sphere (pooled, unit geometry). Size is set by the caller via .scale. */
  private spark(color: number): THREE.Mesh {
    let m = this.sparkPool.pop();
    if (!m) {
      m = new THREE.Mesh(SPARK_GEO, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false }));
    }
    const mat = m.material as THREE.MeshBasicMaterial;
    mat.color.setHex(color);
    mat.blending = THREE.AdditiveBlending;
    mat.opacity = 1;
    this.scene.add(m);
    return m;
  }

  /** Normal-blended dark smoke sphere (pooled, unit geometry). Optional color override for non-smoke uses (e.g. dust). */
  private smoke(color = 0x2b2b2b): THREE.Mesh {
    let m = this.smokePool.pop();
    if (!m) {
      m = new THREE.Mesh(SMOKE_GEO, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.55, depthWrite: false }));
    }
    const mat = m.material as THREE.MeshBasicMaterial;
    mat.color.setHex(color);
    mat.opacity = 0.55;
    this.scene.add(m);
    return m;
  }

  private acquireRing(color: number): THREE.Mesh {
    let m = this.ringPool.pop();
    if (!m) {
      m = new THREE.Mesh(RING_GEO, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.8, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false }));
      m.rotation.x = -Math.PI / 2;
    }
    (m.material as THREE.MeshBasicMaterial).color.setHex(color);
    (m.material as THREE.MeshBasicMaterial).opacity = 0.8;
    this.scene.add(m);
    return m;
  }

  /** Return a sphere to the right pool by its blending mode. */
  private release(m: THREE.Mesh): void {
    this.scene.remove(m);
    const blending = (m.material as THREE.MeshBasicMaterial).blending;
    if (blending === THREE.AdditiveBlending) this.sparkPool.push(m);
    else this.smokePool.push(m);
  }
}
